// ContactFormStyles.js
import styled from "styled-components";
import OrnamentImg from "../../assets/images/ornament.png";

/* Paletă */
export const colors = {
  bg: "#000000",
  text: "#1a1a1a",
  white: "#ffffff",
  accent: "#8b8064",
  gold: "#8b8064",
  line: "rgba(0,0,0,0.08)",
};

/* Card full-bleed pe mobil + spațiu de safe-area */
export const ContactCard = styled.div`
  position: relative;
  background: ${colors.white};
  border-radius: 20px;
  padding: 10px 10px 8px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.4);
  overflow: visible;
  isolation: isolate;
  max-width: 700px;
  margin: 0 auto;

  & > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 768px) {
    border-radius: 16px;
    box-shadow: 0 10px 34px rgba(0, 0, 0, 0.16);
    margin-left: max(5px, env(safe-area-inset-left));
    margin-right: max(5px, env(safe-area-inset-right));
  }

  /* pattern discret + chenar punctat */
  &::before {
    content: "";
    position: absolute;
    inset: 6px;
    border-radius: 14px;
    opacity: 0.04;
    background-image: url("data:image/svg+xml;utf8,\
      <svg xmlns='http://www.w3.org/2000/svg' width='48' height='56' viewBox='0 0 48 56' fill='none'>\
        <g stroke='black' stroke-width='1'>\
          <path d='M12 2 L36 2 L48 22 L36 42 L12 42 L0 22 Z'/>\
          <path d='M12 42 L36 42 L48 62 L36 82 L12 82 L0 62 Z' transform='translate(0,-20)'/>\
          <path d='M-12 22 L12 22 L24 42 L12 62 L-12 62 L-24 42 Z'/>\
        </g>\
      </svg>");
    background-size: 48px 56px;
    background-repeat: repeat;
    pointer-events: none;
    z-index: 0;
  }

  &::after {
    content: "";
    position: absolute;
    inset: 8px;
    border-radius: 14px;
    pointer-events: none;
    border: 1px dashed rgba(0, 0, 0, 0.14);
    z-index: 0;
  }
`;

/* ————— Header ————— */
export const Header = styled.div`
  position: relative;
  padding: 34px 42px 8px;
  text-align: center;
  flex: 0 0 auto;

  @media (max-width: 768px) {
    padding: 28px 20px 6px;
  }
  @media (max-width: 480px) {
    padding: 24px 16px 4px;
  }
`;

export const Ribbon = styled.div`
  position: absolute;
  top: -18px;
  left: 50%;
  transform: translateX(-50%);
  width: 44px;
  height: 44px;
  border-radius: 999px;
  background: radial-gradient(
    circle at 30% 30%,
    #fff 0%,
    #fff 45%,
    #f2f2f2 60%,
    #e9e9e9 100%
  );
  box-shadow: inset 0 0 0 2px ${colors.gold}, 0 8px 22px rgba(0, 0, 0, 0.25);

  @media (max-width: 480px) {
    width: 38px;
    height: 38px;
    top: -14px;
  }
`;

export const Subtitle = styled.div`
  font-size: 17px;
  color: ${colors.gold};
  font-style: italic;
  letter-spacing: 0.3px;
  margin-bottom: 6px;

  @media (max-width: 480px) {
    font-size: 15px;
    margin-bottom: 4px;
  }
`;

export const Title = styled.h3`
  margin: 0;
  font-size: 32px;
  line-height: 1.15;
  font-weight: 800;
  letter-spacing: 0.2px;
  color: #172033;
  font-family: "Georgia", "Times New Roman", Times, serif;

  @media (max-width: 768px) {
    font-size: 28px;
  }
  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

export const HeaderOrnament = styled.div`
  width: 180px;
  height: 22px;
  margin: 6px auto 0;
  background: url(${OrnamentImg}) center/contain no-repeat;
  opacity: 0.95;

  @media (max-width: 480px) {
    width: 150px;
    height: 18px;
  }
`;

export const OrnDivider = styled.div`
  --scale: ${({ $scale }) => $scale || 1};
  --w: calc(400px * var(--scale));
  --h: calc(35px * var(--scale));

  position: relative;
  height: var(--h);
  width: min(100%, var(--w));
  margin: 10px auto 4px;
  background: url(${OrnamentImg}) center / contain no-repeat;
  opacity: 0.95;
`;

/* ————— Form & câmpuri ————— */
export const Form = styled.form`
  padding: 16px 24px 24px;

  @media (min-width: 768px) {
    padding: 20px 36px 32px;
  }
  @media (max-width: 768px) {
    padding: 14px 16px 18px;
  }
  @media (max-width: 480px) {
    padding: 12px 12px 16px;
  }
`;

export const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
  margin-bottom: 8px;

  @media (min-width: 900px) {
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }
  @media (max-width: 480px) {
    gap: 12px;
    margin-bottom: 6px;
  }
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
`;

export const Label = styled.label`
  font-size: 12px;
  font-weight: 700;
  color: ${colors.gold};
  margin-bottom: 6px;
  letter-spacing: 0.35px;
  text-transform: uppercase;

  @media (max-width: 480px) {
    font-size: 11px;
    margin-bottom: 6px;
  }
`;

/* Bază input optimizată touch (min 16px pt iOS no-zoom) */
const baseInput = `
  width: 100%;
  border: 1px solid ${colors.line};
  background: #fff;
  border-radius: 14px;
  padding: 13px 14px;
  font-size: 16px;
  outline: none;
  transition: border-color .2s ease, box-shadow .2s ease, background .2s ease, transform .06s ease;
  box-shadow: 0 2px 6px rgba(0,0,0,0.08);

  &:focus {
    border-color: ${colors.accent};
    box-shadow: 0 0 0 3px rgba(139,128,100,0.25), 0 2px 6px rgba(0,0,0,0.1);
    background: #fff;
  }
  &:active {
    transform: translateY(0.5px);
  }
  &::placeholder { color: rgba(0,0,0,0.35); }

  @media (max-width: 480px) {
    padding: 12px 12px;
    font-size: 16px; /* menține 16px pentru a evita zoom pe iOS */
    border-radius: 12px;
  }
`;

export const Input = styled.input`
  ${baseInput}
`;

export const Select = styled.select`
  ${baseInput};
  appearance: none;
  background-image: linear-gradient(45deg, transparent 50%, ${colors.gold} 50%),
    linear-gradient(135deg, ${colors.gold} 50%, transparent 50%);
  background-position: calc(100% - 22px) 55%, calc(100% - 16px) 55%;
  background-size: 7px 7px, 7px 7px;
  background-repeat: no-repeat;

  @media (max-width: 480px) {
    background-position: calc(100% - 18px) 55%, calc(100% - 12px) 55%;
  }
`;

export const Textarea = styled.textarea`
  ${baseInput};
  resize: vertical;
  min-height: 120px;

  @media (max-width: 480px) {
    min-height: 100px;
  }
`;

export const Helper = styled.div`
  font-size: 12.5px;
  color: rgba(0, 0, 0, 0.55);
  margin-top: 6px;

  @media (max-width: 480px) {
    font-size: 12px;
    margin-top: 5px;
  }
`;

export const Divider = styled.hr`
  height: 0;
  border: 0;
  border-top: 1px dashed rgba(0, 0, 0, 0.12);
  margin: 14px 0 10px;

  @media (max-width: 480px) {
    margin: 12px 0 8px;
  }
`;

export const Actions = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  padding-top: 8px;
  padding-bottom: 4px;
  flex: 0 0 auto;

  @media (max-width: 480px) {
    gap: 8px;
    padding-top: 8px;
  }
`;

const baseBtn = `
  appearance: none;
  border: 0;
  border-radius: 14px;
  font-weight: 800;
  letter-spacing: .2px;
  font-size: 16px;
  padding: 12px 16px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  line-height: 1;

  @media (max-width: 480px) {
    font-size: 16px;
    padding: 13px 16px;
    border-radius: 12px;
    gap: 8px;
  }
`;

export const SubmitBtn = styled.button`
  ${baseBtn}
  background: ${colors.gold};
  color: #fff;
  min-width: 220px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.14);

  svg {
    display: block;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    box-shadow: none;
  }
  &:hover:not(:disabled) {
    filter: brightness(0.98);
    transform: translateY(-0.5px);
  }

  @media (max-width: 768px) {
    min-width: 100%;
    justify-content: center;
  }
`;

export const ErrorMsg = styled.div`
  color: #c02b2b;
  font-size: 13px;
  margin-top: 6px;

  @media (max-width: 480px) {
    font-size: 12.5px;
    margin-top: 5px;
  }
`;

export const SuccessMsg = styled.p`
  margin-top: 12px;
  color: #2f7a3e;
  text-align: center;

  @media (max-width: 480px) {
    margin-top: 10px;
  }
`;
